import { ArrowDropDown } from '@mui/icons-material';
import {
    ClickAwayListener,
    Grow,
    MenuItem,
    MenuList,
    Paper,
    Popper,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip
} from '@mui/material';
import { findIndex } from 'lodash';
import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { useStyling } from '../Theme';
import { BreakpointSize } from '../hooks/breakpoints';
import { useCurrentBreakpoint } from '../hooks/useCurrentBreakpoint';

export type ResponsiveButtonGroupOption = {
    id: string;
    label: string;
    icon: ReactElement;
};

type ResponsiveButtonGroupImplProps = {
    value: string;
    setValue: (values: string) => void;
    options: ResponsiveButtonGroupOption[];
    onToggle: (value: string) => void;
};

const DesktopResponsiveButtonGroup = ({ value, setValue, options, onToggle }: ResponsiveButtonGroupImplProps) => {
    return (
        <ToggleButtonGroup
            value={value || ''}
            exclusive
            onChange={(e, value) => {
                setValue(value);
                onToggle(value);
            }}
        >
            {options.map((option: ResponsiveButtonGroupOption) => {
                return (
                    <ToggleButton key={option.id} value={option.id}>
                        <Tooltip title={option.label}>{option.icon}</Tooltip>
                    </ToggleButton>
                );
            })}
        </ToggleButtonGroup>
    );
};

const MobileResponsiveButtonGroup = ({ value, setValue, options, onToggle }: ResponsiveButtonGroupImplProps) => {
    const { theme } = useStyling();
    const [open, setOpen] = React.useState(false);
    const anchorRef = React.useRef<HTMLDivElement>(null);
    const selectedIndex = useMemo(() => {
        if (value) {
            const index = findIndex(options, { id: value });
            return index > 0 ? index : 0;
        } else {
            return 0;
        }
    }, [options, value]);

    const handleMenuItemClick = (index, option: ResponsiveButtonGroupOption) => {
        const newValue = value === option.id ? null : option.id;
        setValue(newValue);
        onToggle(newValue);
        setOpen(false);
    };

    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };

    const handleClose = (event: Event) => {
        if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
            return;
        }

        setOpen(false);
    };

    return (
        <React.Fragment>
            <ToggleButtonGroup ref={anchorRef}>
                <ToggleButton value={options[selectedIndex].label}>{options[selectedIndex].icon}</ToggleButton>
                <ToggleButton value={'arrow'} size="small" onClick={handleToggle}>
                    <ArrowDropDown color={'disabled'} />
                </ToggleButton>
            </ToggleButtonGroup>
            <Popper
                sx={{
                    zIndex: 999
                }}
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal
            >
                {({ TransitionProps, placement }) => (
                    <Grow
                        {...TransitionProps}
                        style={{
                            transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom'
                        }}
                    >
                        <Paper>
                            <ClickAwayListener onClickAway={handleClose}>
                                <MenuList id="split-button-menu" autoFocusItem>
                                    {options.map((option, index) => (
                                        <MenuItem
                                            key={option.id}
                                            selected={index === selectedIndex}
                                            onClick={() => {
                                                handleMenuItemClick(index, option);
                                            }}
                                            sx={{ gap: 1, backgroundColor: theme.palette.background.paper }}
                                        >
                                            {option.icon}
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </MenuList>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>
        </React.Fragment>
    );
};

type ResponsiveButtonGroupProps = {
    options: ResponsiveButtonGroupOption[];
    selectedOption: string;
    onToggle: (newValue: string) => void;
    useMobileWithBreakpoints: BreakpointSize[];
};

export default ({ options, selectedOption, onToggle, useMobileWithBreakpoints }: ResponsiveButtonGroupProps) => {
    const [value, setValue] = useState<string>(selectedOption);
    useEffect(() => {
        setValue(selectedOption);
    }, [selectedOption]);
    const bp = useCurrentBreakpoint();
    const isSmallScreen = useMobileWithBreakpoints.includes(bp);

    return isSmallScreen && options.length > 1 ? (
        <MobileResponsiveButtonGroup options={options} value={value} setValue={setValue} onToggle={onToggle} />
    ) : (
        <DesktopResponsiveButtonGroup options={options} value={value} setValue={setValue} onToggle={onToggle} />
    );
};
